import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  useAuth,
  useI18N,
  useCreatePublicVenueNote,
  VenueDetailsBySlugResponse,
} from "common/hooks";

import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon } from "lucide-react";
import { Textarea } from "@/shadcn/components/ui/textarea";
import WideButton from "@/components/common/WideButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import SelectDropDown from "@/components/common/SelectDropDown";
import Checkbox from "@/components/common/Checkbox";
import { Button } from "@/shadcn/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/shadcn/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/shadcn/components/ui/popover";
import { DateRange } from "react-day-picker";
import { BusinessSociabilitySaysApproved } from "common/types";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";

function AddVenueNote(props: {
  venueDetailsReponse: VenueDetailsBySlugResponse;
  onClose: () => void;
  onInfoClick: () => void;
  note?: BusinessSociabilitySaysApproved;
}) {
  const i18n = useI18N();
  const { user } = useAuth();

  const showToast = useCustomToast();

  const [notes, setNotes] = useState(props.note?.business_note_text || "");   
  const { data: mutateResult, error, status, mutate } = useCreatePublicVenueNote();
  const noteRef = useRef(null);
  const isDark = useDarkMode();
  const [applyToWholeVenue, setApplyToWholeVenue] = useState(props.note?.location_type === "Business");
  
  // Find the initial area details if there's an existing note
  const initialAreaDetails = useMemo(() => {
    if (props.note?.location_id) {
      // First try to find the section that contains the location_id
      const section = props.venueDetailsReponse.details.businessVenueNotesAreaLocations.find(
        (e) => e.id.toString() === props.note?.location_id.toString()
      );

      if (section) {
        // If we found the section, use its first tag as the subId
        return {
          parentId: Number(section.id),
          subId: section.tags[0]?.id.toString() || ""
        };
      }

      // If we didn't find the section, try to find a tag that matches the location_id
      for (const section of props.venueDetailsReponse.details.businessVenueNotesAreaLocations) {
        const matchingTag = section.tags.find(
          (tag) => tag.id.toString() === props.note?.location_id.toString()
        );
        if (matchingTag) {
          return {
            parentId: Number(section.id),
            subId: matchingTag.id.toString()
          };
        }
      }
    }
    return null;
  }, [props.note?.location_id, props.venueDetailsReponse.details.businessVenueNotesAreaLocations]);

  const [selectedArea, setSelectedArea] = useState<{ parentId: number; subId: string } | null>(initialAreaDetails);
  const [selectedPriority, setSelectedPriority] = useState(props.note?.business_notes_action_id || 0);
  const [hasEndDate, setHasEndDate] = useState(!!props.note?.schedule_date_to);
  const [date, setDate] = useState<Date | undefined>(props.note?.schedule_date_from ? new Date(props.note.schedule_date_from) : undefined);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    props.note?.schedule_date_from && props.note?.schedule_date_to 
      ? { from: new Date(props.note.schedule_date_from), to: new Date(props.note.schedule_date_to) }
      : undefined
  );
  const [title, setTitle] = useState(props.note?.business_note_title || "");

  const selectedAreaDetails = useMemo(() => {
    if (selectedArea) {
      const section = props.venueDetailsReponse.details.businessVenueNotesAreaLocations.find(
        (e) => e.id.toString() === selectedArea.parentId.toString()
      );
      const subSection = section?.tags.find(
        (e) => e.id.toString() === selectedArea.subId
      );
      return {
        parentId: selectedArea.parentId,
        parentName: section?.name,
        subId: selectedArea.subId,
        subName: subSection?.name,
        location_type: section?.location_type,
      };
    }
    return undefined;
  }, [selectedArea, props.venueDetailsReponse.details.businessVenueNotesAreaLocations]);

  useEffect(() => {

    if (error) {
      AnalyticsService.trackGAEventHandler("mapperNoteReportError");
      AnalyticsService.trackMx("mapperNoteReportError", {
        VenueId: props.venueDetailsReponse.details.id,
        VenueBrandId: props.venueDetailsReponse.details.brandId,
        VenueBrandName: props.venueDetailsReponse.details.brandName,
        VenueAddress: props.venueDetailsReponse.details.address,
        VenueName: props.venueDetailsReponse.details.name,
        VenueTypes: props.venueDetailsReponse.details.types,
      });

      showToast(error.message, "error");
    }
  }, [error]);

  useEffect(() => {
    if (mutateResult) {      
      console.log("mutateResult", mutateResult);
      showToast(props.note ? i18n.get("noteUpdatedSuccessfully") : i18n.get("noteCreatedSuccessfully"), "success");
      setNotes("");
      setSelectedPriority(0);
      props.onClose();
    }
  }, [mutateResult]);

  const submit = () => {
    AnalyticsService.trackGAEventHandler("mapperNoteReportCreate");
    AnalyticsService.trackMx("mapperNoteReportCreate", {
      VenueId: props.venueDetailsReponse.details.id,
      VenueBrandId: props.venueDetailsReponse.details.brandId,
      VenueBrandName: props.venueDetailsReponse.details.brandName,
      VenueAddress: props.venueDetailsReponse.details.address,
      VenueName: props.venueDetailsReponse.details.name,
      VenueTypes: props.venueDetailsReponse.details.types,
      ReviewerName: user?.name,
    });

    const selectedPriorityDetails =
      props.venueDetailsReponse.details.businessNotesActionsVenueNotes.find(
        (e) => e.id.toString() === selectedPriority.toString()  
      );

    let locationId = props.venueDetailsReponse.details.business.id.toString();
    let locationType = "Business";
    if (!applyToWholeVenue && selectedAreaDetails) {
      locationId = selectedAreaDetails?.subId
      ? selectedAreaDetails?.subId.toString()
      : selectedAreaDetails?.parentId.toString();
      locationType = selectedAreaDetails.location_type || "";
    }

    mutate({
      noteId: props.note?.business_note_text_id?.toString() || "",
      businessId: props.venueDetailsReponse.details.business.id.toString() || "",
      note: notes,
      title: title,
      notePriority: selectedPriorityDetails?.id.toString() || "",
      noteAreaType: locationType || "",
      noteAreaId: locationId || "",
      dateFrom: date ? format(date, "yyyy-MM-dd") : dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : "",
      dateTo: dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : "",
    });

  };

  const handleOptionPress = (id: number) => {
    console.log("handleOptionPress", id);
    setSelectedPriority(id);

    console.log("handleOptionPress selectedOption", id);

  };

  const isSubmitDisabled = () => {
    // Additional conditions for other options
    return selectedPriority === 0 || title.length < 5;
  };


  return (
    <>
      <section
        className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(100vh-120px)] md:h-[calc(100vh-200px)] overflow-hidden"
        role="none"
      >
        {status === "pending" && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-[250px] z-50">
            <Loader />
          </div>
        )}

        <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 vsticky top-0">
          <h1
            className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
            tabIndex={0}
          >
            {props.note ? i18n.get("editNote") : i18n.get("createNote")}
          </h1>
          <button
            onClick={props.onClose}
            className="absolute top-4 left-4"
            aria-label="Close modal"
          >
            <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
          </button>
        </header>

        <div
          aria-label="Modal Content"
          className="flex-grow overflow-y-auto"
          style={{ WebkitOverflowScrolling: "touch" }}
          role="none"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div
                  className="self-stretch !text-primary-500 dark:!text-primary-300 paragraph-xsmall-semibold"
                  tabIndex={0}
                >
                  {props.venueDetailsReponse.details.name}
                </div>
                <div
                  className="self-stretch text-primary-800 dark:text-primary-150 desktop-h4-medium-bold"
                  tabIndex={0}
                >
                  {i18n.get("publicNote")}
                </div>
              </div>
              <div
                className="self-stretch text-primary-800 dark:text-primary-150 paragraph-small-semibold md:desktop-h4-medium-semi-bold pt-1"
                tabIndex={0}
              >
                {i18n.get("shareYourInsightsToHelp")}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className=" w-full " tabIndex={0}>              
                <FloatingLabelInput
                    label={i18n.get("title")}
                    required
                    type="text"
                    value={title}
                    placeholder={i18n.get("addNoteHeadline")}
                    aria-label={i18n.get("addNoteHeadline")}
                    onChange={(event) => {
                      setTitle(event.target.value);
                    }}
                  />                  
                <div
                  id="charCountTitle"
                  className="button-small-medium text-neutral-500 mt-3 mb-1"
                  tabIndex={0}
                >
                  <span className={title.length < 5 ? "text-red-600" : ""}>
                    {title.length}
                  </span>
                  /50 Characters - at least 5 characters required
                </div>
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className=" w-full " tabIndex={0}>
                <Textarea
                  id="review"
                  placeholder={i18n.get("addNotesHere")}
                  className="scrollbar-thin dark:scrollbar-thin-dark h-28"
                  value={notes}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setNotes(e.target.value);
                  }}
                  maxLength={150}
                  ref={noteRef}
                  required={false}
                />
                <div
                  id="charCount"
                  className="button-small-medium text-neutral-500 mt-3"
                  tabIndex={0}
                >
                  <span>
                    {notes.length}
                  </span>
                  /150 Characters
                </div>
              </div>
            </div>


            {/* Apply whole venue */}
            <div className="self-stretch flex flex-col gap-2 pt-3">
              <h2 className="mobile-h3-small-semi-bold text-primary-800 dark:text-primary-150">
                {i18n.get("doesTheNoteApplyToTheWholeVenue")}
              </h2>
              <div className="flex gap-4">
                <Checkbox
                  checked={applyToWholeVenue}
                  onCheck={(value) => {
                    if (!applyToWholeVenue && value) {
                      setApplyToWholeVenue(true);
                      setSelectedArea(null);
                    }
                  }}
                  label="Yes"
                />
                <Checkbox
                  checked={!applyToWholeVenue}
                  onCheck={(value) => {
                    if (applyToWholeVenue && value) {
                      setApplyToWholeVenue(false);
                    }
                  }}
                  label="No"
                />
              </div>
            </div>

            {/* Area Selection - Only shown when not applying to whole venue */}
            {!applyToWholeVenue && (
              <div className="self-stretch flex flex-col gap-2 mt-4">
                <SelectDropDown
                  label={i18n.get("selectNoteLocation")}
                  optionsList={[
                    { id: "0", label: i18n.get("selectArea") },
                    ...props.venueDetailsReponse.details.businessVenueNotesAreaLocations
                      .filter((area) => area.id !== "Business")
                      .map((area) => ({
                        id: area.id.toString(),
                        label: area.name,
                        subTitles: area.tags.map((tag) => ({
                          id: tag.id.toString(),
                          label: tag.name,
                        })),
                      })),
                  ]}
                  value={selectedArea ? `${selectedArea.parentId}.${selectedArea.subId}` : "0"}
                  required={true}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "0") {
                      setSelectedArea(null);
                      return;
                    }
                    const [parentId, subId] = value.split('.');
                    if (parentId && subId) {
                      setSelectedArea({ 
                        parentId: Number(parentId), 
                        subId 
                      });
                    }
                  }}
                />
              </div>
            )}

            {/* Priority Level Selection */}
            <div className="self-stretch rounded-lg">
              <div className="mb-2">
                <div className="flex flex-col mt-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <SelectDropDown
                        label={i18n.get("selectNotePriority")}
                        optionsList={[
                          { id: 0, label: i18n.get("selectPriorityLevel") },
                          ...(
                            props.venueDetailsReponse.details
                              .businessNotesActionsVenueNotes || []
                          ).map((option) => ({
                            id: option.id,
                            label: option.title,
                          })),
                        ]}
                        value={selectedPriority}
                        required={true}
                        onChange={(e) =>
                          handleOptionPress(Number(e.target.value))
                        }
                      />
                    </div>
                    <img
                      src={
                        isDark ? "/images/Info_dark.svg" : "/images/Info.svg"
                      }
                      alt=""
                      aria-hidden="true"
                      className="w-6 h-6 self-start mt-3 cursor-pointer"
                      onClick={props.onInfoClick}
                    />
                  </div>
                </div>
              </div>
            </div>

          



            {/* End Date */}
            <div className="self-stretch flex flex-col gap-2 pt-5">
              <h2 className="mobile-h3-small-semi-bold text-primary-800 dark:text-primary-150">
                {i18n.get("doesTheNoteHaveAnEndDate")}
              </h2>
              <div className="flex gap-4">
                <Checkbox
                  checked={hasEndDate}
                  onCheck={(value) => {
                    if (!hasEndDate && value) {
                      setHasEndDate(true);
                      setDate(undefined);
                      setDateRange(undefined);
                    }
                  }}
                  label="Yes"
                />
                <Checkbox
                  checked={!hasEndDate}
                  onCheck={(value) => {
                    if (hasEndDate && value) {
                      setHasEndDate(false);
                      setDate(undefined);
                      setDateRange(undefined);
                    }
                  }}
                  label="No"
                />
              </div>
            </div>

            {/* Conditional Rendering of Date Selection */}
            {hasEndDate && (
              <div className="my-4 self-stretch">
                <div className="flex flex-col gap-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className={`w-full justify-start text-left font-normal ${
                          !dateRange?.from &&
                          !dateRange?.to &&
                          "text-muted-foreground"
                        }`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from && dateRange?.to ? (
                          <>
                            {format(dateRange.from, "LLL dd, y")} -{" "}
                            {format(dateRange.to, "LLL dd, y")}
                          </>
                        ) : dateRange?.from ? (
                          <>
                            {format(dateRange.from, "LLL dd, y")} - No end date
                            set
                          </>
                        ) : (
                          <span>No date range selected</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-4 z-[9999] bg-white text-black rounded-md shadow-lg border"
                      align="start"
                      sideOffset={5}
                    >
                      <Calendar
                        mode="range"
                        required={true}
                        selected={dateRange}
                        onSelect={(selected) => {
                          if (
                            !dateRange?.from ||
                            (dateRange.from && dateRange.to)
                          ) {
                            // If no date selected or both dates are selected, start new selection
                            setDateRange({
                              from: selected?.from,
                              to: undefined,
                            });
                          } else {
                            // If we have a from date but no to date, complete the range
                            if (selected?.from && selected?.to) {
                              setDateRange(selected);
                            }
                          }
                        }}
                        initialFocus
                        disabled={(date) =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky bottom-0 bg-white dark:bg-primary-900  z-1000">
          <div className="flex-col justify-end items-center gap-4 flex w-[90%] md:w-full md:max-w-[70%]">
            <div className="w-[327px] justify-center items-center inline-flex">
              <WideButton
                disabled={isSubmitDisabled()} // Disable if conditions are not met
                className="grow"
                onClick={() => {
                  submit();
                }}
                aria-label="Submit Mapper note"
              >
                {i18n.get("submit")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </>
  );
}

export default AddVenueNote;
